import { z } from "zod";
import { AssumptionIdSchema } from "./branded-id.schemas.js";

/**
 * Single-sourced assumption-category taxonomy shared by the directory AI
 * generator and the participant-assumptions field so the two cannot drift.
 */
export const ASSUMPTION_CATEGORIES = [
  "professional_context",
  "networking_objectives",
  "event_engagement",
  "knowledge_learning",
  "contribution_potential",
  "operational_team",
  "expansion_geo",
  "identity_clarification",
] as const;

export const AssumptionCategorySchema = z.enum(ASSUMPTION_CATEGORIES);
export type AssumptionCategory = z.infer<typeof AssumptionCategorySchema>;

export const AssumptionDataSufficiencySchema = z.enum([
  "rich",
  "adequate",
  "minimal",
]);
export const AssumptionStatusSchema = z.enum({
  INFERRED: "inferred",
  CONFIRMED: "confirmed",
  // RETIRED — nothing writes these; only items stored before the intake refiner
  // was removed carry them, and the engine's allow-list still excludes both.
  REFUTED: "refuted",
  REFINED: "refined",
});
export type AssumptionStatus = z.infer<typeof AssumptionStatusSchema>;

export const AssumptionStatus = AssumptionStatusSchema.enum;

// `intake` is RETIRED — enrichment is the only writer left; only items stored
// before the intake refiner was removed carry it.
export const AssumptionOriginSchema = z.enum(["enrichment", "intake"]);
export const participantAssumptionItemSchema = z.object({
  id: AssumptionIdSchema,
  statement: z.string(),
  confidence: z.number().min(0).max(100),
  category: AssumptionCategorySchema,
  status: AssumptionStatusSchema,
  origin: AssumptionOriginSchema,
  updatedAt: z.coerce.date(),
});
export type ParticipantAssumptionItem = z.infer<
  typeof participantAssumptionItemSchema
>;

export const participantAssumptionsSchema = z.object({
  dataSufficiency: AssumptionDataSufficiencySchema,
  // Watermark for the lazy-refine "owed" gate: stamped on every refine
  // (including no-ops), null until the first refine runs. Lives on the block
  // (not the participant) so the watermark travels with the assumptions.
  refinedAt: z.coerce.date().nullable(),
  items: z.array(participantAssumptionItemSchema).readonly(),
});
export type ParticipantAssumptions = z.infer<
  typeof participantAssumptionsSchema
>;
